import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import LanguageSelect from "@/components/LanguageSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, remember);
      navigate("/kalender");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-600 font-heading text-lg font-bold text-white">
            FM
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">FältMästare</h1>
            <p className="text-xs uppercase tracking-[0.05em] text-slate-500">{t("login.subtitle")}</p>
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3" data-testid="language-picker">
          <p className="mb-1.5 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
            {t("login.chooseLanguage")}
          </p>
          <LanguageSelect className="w-full" />
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="rounded-lg border border-slate-200 bg-white p-5"
        >
          <h2 className="mb-4 text-xl font-semibold text-slate-800">{t("login.title")}</h2>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
                {t("login.email")}
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="username"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-10"
                placeholder="namn@foretag.se"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
                {t("login.password")}
              </Label>              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-10"
                placeholder="••••••••"
              />
            </div>
          </div>

          <label
            htmlFor="remember"
            className="mt-4 flex cursor-pointer items-center gap-2.5 rounded-md border border-slate-200 bg-slate-50 p-3 transition-colors duration-200 hover:bg-slate-100"
          >
            <Checkbox
              id="remember"
              data-testid="login-remember-checkbox"
              checked={remember}
              onCheckedChange={(v) => setRemember(v === true)}
            />
            <span className="text-sm font-medium text-slate-800">{t("login.remember")}</span>
          </label>

          {error && (
            <p
              data-testid="login-error"
              className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="mt-4 h-11 w-full bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.title")}
          </Button>

          <p className="mt-4 text-sm text-slate-600">
            {t("login.noAccount")}{" "}
            <Link to="/skapa-konto" data-testid="login-register-link" className="font-semibold text-blue-600 hover:text-blue-700">
              {t("register.title")}
            </Link>
          </p>

          <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" /> {t("login.hint")}
          </p>
        </form>
      </div>
    </div>
  );
}
